/**
 * API endpoints para Promotions (Promociones y Descuentos)
 * Ahora parte del módulo de inventario/productos
 * Base URL: /api/inventory/
 */

import { GET, POST, PUT, PATCH, DELETE } from "../config";

// ==================== DESCUENTOS/PROMOCIONES ====================

/**
 * Listar descuentos/promociones
 * @param {number} page - Número de página
 * @param {object} filters - Filtros (branch, status, discount_type, search, etc.)
 * @returns {Promise} Lista de descuentos
 */
export const list = async (page = 1, filters = {}) => {
  let url = `inventory/promotion-discounts/?page=${page}`;
  url += `&page_size=50`;

  if (filters.branch) {
    url += `&branch=${filters.branch}`;
  }

  if (filters.status) {
    url += `&status=${filters.status}`;
  }

  if (filters.discount_type) {
    url += `&discount_type=${filters.discount_type}`;
  }

  if (filters.name) {
    url += `&name__icontains=${encodeURIComponent(filters.name)}`;
  }

  if (filters.code) {
    url += `&code__icontains=${encodeURIComponent(filters.code)}`;
  }

  if (filters.start_date) {
    url += `&start_date__gte=${filters.start_date}`;
  }

  if (filters.end_date) {
    url += `&end_date__lte=${filters.end_date}`;
  }

  if (filters.is_stackable !== undefined) {
    url += `&is_stackable=${filters.is_stackable}`;
  }

  if (filters.search) {
    url += `&name__icontains=${encodeURIComponent(filters.search)}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener un descuento por ID
 * @param {string|number} id - ID del descuento
 * @returns {Promise} Datos del descuento
 */
export const get = async (id) => {
  const response = await GET(`inventory/promotion-discounts/${id}/`);
  return response.data;
};

/**
 * Crear un descuento
 * @param {object} data - Datos del descuento
 * @returns {Promise} Descuento creado
 */
export const create = async (data) => {
  const response = await POST("inventory/promotion-discounts/", data);
  return response.data;
};

/**
 * Actualizar un descuento
 * @param {string|number} id - ID del descuento
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Descuento actualizado
 */
export const update = async (id, data) => {
  const response = await PUT(`inventory/promotion-discounts/${id}/`, data);
  return response.data;
};

/**
 * Actualizar parcialmente un descuento
 * @param {string|number} id - ID del descuento
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Descuento actualizado
 */
export const partialUpdate = async (id, data) => {
  const response = await PATCH(`inventory/promotion-discounts/${id}/`, data);
  return response.data;
};

/**
 * Eliminar un descuento
 * @param {string|number} id - ID del descuento
 * @returns {Promise}
 */
export const remove = async (id) => {
  const response = await DELETE(`inventory/promotion-discounts/${id}/`);
  return response.data;
};

/**
 * Validar un código de descuento
 * @param {string} code - Código del descuento
 * @param {number} branch_id - ID de la sucursal
 * @param {number} order_total - Total de la orden
 * @returns {Promise} Resultado de la validación
 */
export const validateCode = async (code, branch_id, order_total = 0) => {
  const response = await POST("inventory/promotion-discounts/validate_code/", {
    code: code.toUpperCase().trim(),
    branch_id,
    order_total,
  });
  return response.data;
};

/**
 * Aplicar un descuento a una orden
 * @param {string} discount_code - Código del descuento
 * @param {string} order_id - UUID de la orden
 * @returns {Promise} Resultado de la aplicación
 */
export const applyDiscount = async (discount_code, order_id) => {
  const response = await POST("inventory/promotion-discounts/apply_discount/", {
    discount_code: discount_code.toUpperCase().trim(),
    order_id,
  });
  return response.data;
};

/**
 * Obtener estadísticas de descuentos
 * @param {object} filters - Filtros (branch, etc.)
 * @returns {Promise} Estadísticas
 */
export const getStats = async (filters = {}) => {
  let url = "inventory/promotion-discounts/usage_stats/";
  const params = new URLSearchParams();

  if (filters.branch) {
    params.append("branch", filters.branch);
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await GET(url);
  return response.data;
};

// ==================== USO DE DESCUENTOS ====================

/**
 * Listar uso de descuentos
 * @param {number} page - Número de página
 * @param {object} filters - Filtros (discount, order, user, branch)
 * @returns {Promise} Lista de usos
 */
export const listUsage = async (page = 1, filters = {}) => {
  let url = `inventory/promotion-usage/?page=${page}`;
  url += `&page_size=50`;

  if (filters.discount) {
    url += `&discount=${filters.discount}`;
  }

  if (filters.order) {
    url += `&order=${filters.order}`;
  }

  if (filters.user) {
    url += `&user=${filters.user}`;
  }

  if (filters.branch) {
    url += `&discount__branch=${filters.branch}`;
  }

  if (filters.start_date) {
    url += `&created__gte=${filters.start_date}`;
  }

  if (filters.end_date) {
    url += `&created__lte=${filters.end_date}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener un uso de descuento por ID
 * @param {string|number} id - ID del uso
 * @returns {Promise} Datos del uso
 */
export const getUsage = async (id) => {
  const response = await GET(`inventory/promotion-usage/${id}/`);
  return response.data;
};

// ==================== EXPORT ====================

const promotionsApi = {
  // Descuentos/Promociones
  list,
  get,
  create,
  update,
  partialUpdate,
  remove,
  validateCode,
  applyDiscount,
  getStats,
  // Uso de descuentos
  usage: {
    list: listUsage,
    get: getUsage,
  },
};

export default promotionsApi;
